import React, { useState, useEffect, useContext } from 'react';
import './update.css';
import { updateIssue, fetchUsers } from '../../services/apiService';
import Select from 'react-select';
import { UserContext } from '../userContext/UserContext';
import ImageViewer from '../imageViewer/imageViewer';

const Update = ({ issue, closeModal }) => {
    const [updatedIssue, setUpdatedIssue] = useState({
        Status: '',
        Title: '',
        IssueName: '',
        Severity: '',
        ReportedByName: '',
        ReportedById: '',
        DateReported: '',
        AssignedToName: '',
        AssignedToId: '',
        DueDate: '',
        Team: '',
        Description: '',
        AttachmentURL: '',
        LessonsLearned: false, // New field
        IntExt: []           // New field for Internal/External (array)
    });

    const [userMap, setUserMap] = useState({});
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(0);

    const [newAttachments, setNewAttachments] = useState([]);
    const [newAttachmentPreviews, setNewAttachmentPreviews] = useState([]);
    const [deletedAttachments, setDeletedAttachments] = useState([]);

    const [loading, setLoading] = useState(false);
    // Access the role (and sasToken) from UserContext
    const { role, sasToken } = useContext(UserContext);

    useEffect(() => {
        async function getUsers() {
            const users = await fetchUsers();
            const map = {};
            users.forEach(user => {
                map[user.id] = user.displayName;
            });
            setUserMap(map);
        }
        getUsers();

        if (issue) {
            // Clone the issue object to avoid directly mutating props
            const transformedIssue = { ...issue };

            // Ensure AttachmentURL is an array (append sasToken when displaying)
            if (transformedIssue.AttachmentURL) {
                transformedIssue.AttachmentURL = transformedIssue.AttachmentURL
                    .split(';')
                    .map(url => url.trim())
                    .filter(url => url !== '');
            } else {
                transformedIssue.AttachmentURL = [];
            }

            // Set default values for new fields if not provided
            if (transformedIssue.LessonsLearned === undefined) {
                transformedIssue.LessonsLearned = false;
            }
            if (!transformedIssue.IntExt || transformedIssue.IntExt.length === 0) {
                transformedIssue.IntExt = ['Internal']; // Default to Internal selected
            }

            setUpdatedIssue(transformedIssue);
        }
    }, [issue, sasToken]);

    const handleAttachmentChange = (e) => {
        const files = Array.from(e.target.files);
        setNewAttachments([...newAttachments, ...files]);
        setNewAttachmentPreviews([...newAttachmentPreviews, ...files]);
    };

    const handleAttachmentRemove = (index) => {
        const newA = [...newAttachments];
        const newP = [...newAttachmentPreviews];
        newA.splice(index, 1);
        newP.splice(index, 1);
        setNewAttachments(newA);
        setNewAttachmentPreviews(newP);
    };

    const handleExistingAttachmentToggle = (url) => {
        if (deletedAttachments.includes(url)) {
            setDeletedAttachments(deletedAttachments.filter((item) => item !== url));
        } else {
            setDeletedAttachments([...deletedAttachments, url]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUpdatedIssue({
            ...updatedIssue,
            [name]: value,
        });
    };

    // Handler for toggling Internal/External checkboxes
    const handleIntExtCheckbox = (value, isChecked) => {
        setUpdatedIssue(prevIssue => {
            // Ensure at least one checkbox remains selected:
            if (!isChecked && prevIssue.IntExt.length === 1) {
                return prevIssue;
            }
            if (isChecked) {
                return {
                    ...prevIssue,
                    IntExt: prevIssue.IntExt.includes(value)
                        ? prevIssue.IntExt
                        : [...prevIssue.IntExt, value]
                };
            } else {
                return {
                    ...prevIssue,
                    IntExt: prevIssue.IntExt.filter(item => item !== value)
                };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (loading) return; // Prevent multiple clicks
        setLoading(true);

        const assignedToNames = updatedIssue.AssignedToId
            ? updatedIssue.AssignedToId.split(',').map(userId => userMap[userId]).join(', ')
            : null;

        const issueData = {
            ...updatedIssue,
            ReportedByName: updatedIssue.ReportedById ? userMap[updatedIssue.ReportedById] : null,
            AssignedToName: assignedToNames,
            DateReported: updatedIssue.DateReported || null,
            DueDate: updatedIssue.DueDate || null,
            AttachmentURL: updatedIssue.AttachmentURL || [],
            LessonsLearned: updatedIssue.LessonsLearned, // Include updated LessonsLearned
            IntExt: updatedIssue.IntExt                  // Include updated Internal/External array
        };

        // Get the previous assigned users from the original issue prop.
        const previousAssignedUsers = issue.AssignedToId 
            ? issue.AssignedToId.split(',')
            : [];

        try {
            // Pass previousAssignedUsers as an extra parameter.
            await updateIssue(issueData, deletedAttachments, newAttachments, previousAssignedUsers);
            alert("Issue Updated Successfully");
            closeModal();
        } catch (error) {
            console.error('Failed to update issue:', error);
            alert('Failed to update issue');
        } finally {
            setLoading(false); // Re-enable button after completion
        }
    };

    const handleImageClick = (index) => {
        setViewerIndex(index);
        setIsViewerOpen(true);
    };

    const getFileExtension = (url) => {
        const match = url.match(/\.([a-zA-Z0-9]+)(\?|$)/);
        return match ? match[1].toLowerCase() : null;
    };

    return (
        <div className="modal-container">
            <div className="modal-title">Update Issue</div>
            <form className="modal-form" onSubmit={handleSubmit}>

                {/* Job Number, Issue Name, Severity, Status, and Date */}
                {/* Internal/External Checkboxes */}
                <div className="modal-form-row internal-lessons-row">
                    <div className="modal-form-group internal-external-group">
                    <label className="group-label">Internal/External:</label>
                        <div className="checkbox-group">
                        <label>
                        Internal
                        <input
                            type="checkbox"
                            id="updateInternal"
                            checked={updatedIssue.IntExt.includes("Internal")}
                            onChange={(e) => {
                                if (!e.target.checked && updatedIssue.IntExt.length === 1) return;
                                handleIntExtCheckbox("Internal", e.target.checked);
                            }}
                        />
                        
                    </label>
                    <label>
                        External
                        <input
                            type="checkbox"
                            id="updateExternal"
                            checked={updatedIssue.IntExt.includes("External")}
                            onChange={(e) => {
                                if (!e.target.checked && updatedIssue.IntExt.length === 1) return;
                                handleIntExtCheckbox("External", e.target.checked);
                            }}
                        />
                        
                    </label>
                        </div>
                    
                </div>
                    <div className="modal-form-group lessons-learned-group">
                        <label>
                            Lessons Learned
                            <input
                                type="checkbox"
                                name="LessonsLearned"
                                checked={updatedIssue.LessonsLearned}
                                onChange={(e) =>
                                    setUpdatedIssue({ ...updatedIssue, LessonsLearned: e.target.checked })
                                }
                            />
                        </label>
                    </div>
                </div>

                <div className="modal-form-row">
                    <div className="modal-form-group jobnum-group">
                        <label>Job #*:</label>
                        <input 
                            type="text" 
                            name="Title" 
                            value={updatedIssue.Title || ''} 
                            onChange={handleChange} 
                            required
                            disabled={role !== 'Role.Admin'}
                        />
                    </div>
                    <div className="modal-form-group name-group">
                        <label>Issue Name*:</label>
                        <input 
                            type="text" 
                            name="IssueName" 
                            value={updatedIssue.IssueName || ''} 
                            onChange={handleChange} 
                            required
                            disabled={role !== 'Role.Admin'}
                        />
                    </div>
                    <div className="modal-form-group jobnum-group">
                        <label>Severity*:</label>
                        <select 
                            name="Severity" 
                            value={updatedIssue.Severity || ''} 
                            onChange={handleChange} 
                            required
                            disabled={role !== 'Role.Admin'}
                        >
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                        </select>
                    </div>
                    <div className="modal-form-group status-group">
                        <label>Status*:</label>
                        <select 
                            name="Status" 
                            value={updatedIssue.Status || ''}  
                            onChange={handleChange}  
                        >
                            <option value="">Select</option>
                            <option value="Active">Open</option>
                            <option value="Resolved">Resolved</option>
                            {role === 'Role.Admin' && <option value="Closed">Closed</option>}
                        </select>
                    </div>
                    <div className="modal-form-group date-group">
                        <label>Date:</label>
                        <input 
                            type="date" 
                            name="DateReported" 
                            value={updatedIssue.DateReported || ''}  
                            onChange={handleChange}  
                            disabled={role !== 'Role.Admin'}
                        />
                    </div>
                </div>

                {/* Reported By, Assigned To, Team, and Due Date */}
                <div className="modal-form-row">
                    <div className="modal-form-group report-group">
                        <label>Reported By:</label>
                        <select 
                            name="ReportedById" 
                            value={updatedIssue.ReportedById || ''} 
                            onChange={handleChange} 
                            disabled={role !== 'Role.Admin'}
                        >
                            <option value=''>Select User</option>
                            {Object.keys(userMap).map(userId => (
                                <option key={userId} value={userId}>
                                    {userMap[userId]}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Assigned To - Editable by admin and supervisor */}
                    <div className="modal-form-group assign-group">
                        <label>Assigned To:</label>
                        <Select
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    fontSize: '14px',
                                }),
                                option: (provided) => ({
                                    ...provided,
                                    fontSize: '14px',
                                }),
                            }}
                            isMulti
                            name="AssignedToId"
                            value={updatedIssue.AssignedToId ? updatedIssue.AssignedToId.split(',').map(userId => ({ value: userId, label: userMap[userId] })) : []}
                            onChange={(selectedOptions) => {
                                const selectedUserIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                                setUpdatedIssue({
                                    ...updatedIssue,
                                    AssignedToId: selectedUserIds.join(',')
                                });
                            }}
                            options={Object.keys(userMap).map(userId => ({
                                value: userId,
                                label: userMap[userId],
                            }))}
                            isDisabled={role === 'Role.User'}
                            maxMenuHeight={250}
                            menuShouldScrollIntoView={false}
                        />
                    </div>
                    <div className="modal-form-group team-group">
                        <label>Team:</label>
                        <select 
                            name="Team" 
                            value={updatedIssue.Team || ''} 
                            onChange={handleChange}
                            disabled={role !== 'Role.Admin'}
                        >
                            <option value="">Select</option>
                            <option value="Mechanical Design">Mechanical Design</option>
                            <option value="Controls Design">Controls Design</option>
                            <option value="Mechanical Build">Mechanical Build</option>
                            <option value="Electrical Build">Electrical Build</option>
                            <option value="Project Management">Project Management</option>
                        </select>
                    </div>
                    
                    <div className="modal-form-group date-group">
                        <label>Due Date:</label>
                        <input 
                            type="date" 
                            name="DueDate" 
                            value={updatedIssue.DueDate || ''}  
                            onChange={handleChange}  
                            disabled={role !== 'Role.Admin'}
                        />
                    </div>
                </div>

                {/* Description */}
                <div className="modal-form-group">
                    <label>Description:</label>
                    <textarea 
                        name="Description" 
                        value={updatedIssue.Description || ''}  
                        onChange={handleChange}  
                    />
                </div>
                

                {/* Attachments Section */}
                <div className="form-group">
                    <label>Attachments:</label>
                    <input
                        type="file"
                        multiple
                        onChange={handleAttachmentChange}
                    />
                    {updatedIssue.AttachmentURL.length > 0 && (
                        <div className="attachment-preview-container">
                            <div className="attachments-section-title">Existing Attachments</div>
                            <div className="attachments-container">
                                {updatedIssue.AttachmentURL.map((preview, index) => {
                                    const fileExtension = getFileExtension(preview);
                                    const isRemoved = deletedAttachments.includes(preview);
                                    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExtension)) {
                                        return (
                                            <div key={index} className="attachment-preview">
                                                <img
                                                    src={`${preview}?${sasToken}`}
                                                    alt="Preview"
                                                    className={`attachment-image ${isRemoved ? 'removed' : ''}`}
                                                    onClick={() => handleImageClick(index)}
                                                />
                                                {isRemoved && (
                                                    <div className="overlay">Removed</div>
                                                )}
                                                <button
                                                    type="button"
                                                    className="remove-attachment-button"
                                                    onClick={() => handleExistingAttachmentToggle(preview)}
                                                >
                                                    {isRemoved ? '↺' : '×'}
                                                </button>
                                            </div>
                                        );
                                    } else if (['mp4', 'webm', 'ogg'].includes(fileExtension)) {
                                        return (
                                            <div
                                                key={index}
                                                className="attachment-preview video-preview"
                                                onClick={() => handleImageClick(index)}
                                            >
                                                <div className="video-play-icon">&#9658;</div>
                                                {isRemoved && <div className="overlay">Removed</div>}
                                                <button
                                                    type="button"
                                                    className="remove-attachment-button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleExistingAttachmentToggle(preview);
                                                    }}
                                                >
                                                    {isRemoved ? '↺' : '×'}
                                                </button>
                                            </div>
                                        );
                                    } else {
                                        return (
                                            <div
                                                key={index}
                                                className="attachment-preview file-preview"
                                                onClick={() => handleImageClick(index)}
                                            >
                                                <div className="file-icon">&#128196;</div>
                                                {isRemoved && (
                                                    <div className="overlay">Removed</div>
                                                )}
                                                <button
                                                    type="button"
                                                    className="remove-attachment-button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleExistingAttachmentToggle(preview);
                                                    }}
                                                >
                                                    {isRemoved ? '↺' : '×'}
                                                </button>
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        </div>
                    )}
                    {newAttachmentPreviews.length > 0 && (
                        <div className="attachment-preview-container">
                            <div className="attachments-section-title">New Attachments</div>
                            <div className="attachments-container">
                                {newAttachmentPreviews.map((file, index) => {
                                    const fileType = file.type || '';
                                    const fileUrl = URL.createObjectURL(file);
                                    if (fileType.startsWith('image/')) {
                                        return (
                                            <div key={index} className="attachment-preview">
                                                <img
                                                    src={fileUrl}
                                                    alt="Preview"
                                                    className="attachment-image"
                                                />
                                                <button
                                                    type="button"
                                                    className="remove-attachment-button"
                                                    onClick={() => handleAttachmentRemove(index)}
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        );
                                    } else if (fileType.startsWith('video/')) {
                                        return (
                                            <div key={index} className="attachment-preview video-preview">
                                                <div className="video-play-icon">&#9658;</div>
                                                <button
                                                    type="button"
                                                    className="remove-attachment-button"
                                                    onClick={() => handleAttachmentRemove(index)}
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        );
                                    } else {
                                        return (
                                            <div key={index} className="attachment-preview file-preview">
                                                <div className="file-icon">&#128196;</div>
                                                <button
                                                    type="button"
                                                    className="remove-attachment-button"
                                                    onClick={() => handleAttachmentRemove(index)}
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <button type="submit" className="submit-button" disabled={loading}>
                    {loading ? 'Updating...' : 'Update Issue'}
                </button>
            </form>

            {isViewerOpen && (
                <ImageViewer
                    images={updatedIssue.AttachmentURL}
                    initialIndex={viewerIndex}
                    onClose={() => setIsViewerOpen(false)}
                />
            )}
        </div>
    );
};

export default Update;
